import { useEffect, useRef, useState } from "react";
import { auth } from "../lib/firebase";
import { FUNCTION_URLS, type LoggedFunctionName } from "../lib/config";
import type { LogEntry, LogsResponse } from "../lib/types";

const POLL_INTERVAL_MS = 3000;
// Linger after the run finishes so trailing logs (final summary line, etc.)
// have a chance to flush from Cloud Logging — entries are usually visible
// within a few seconds.
const POLL_LINGER_MS = 15_000;

export interface FunctionLogsState {
  entries: LogEntry[];
  isPolling: boolean;
  lastError: string | null;
  reset: () => void;
}

/**
 * Poll Cloud Logging via cf_function_logs while *active* is true, plus a short
 * grace period after to catch trailing entries. Entries accumulate across
 * polls; reset() clears them (call before starting a new run).
 */
export function useFunctionLogs(
  functionName: LoggedFunctionName,
  startTime: Date | null,
  active: boolean,
): FunctionLogsState {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const sinceRef = useRef<string | null>(null);
  const activeRef = useRef(active);

  // Keep the active flag fresh inside the polling loop without restarting it.
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    if (!startTime) return;

    sinceRef.current = startTime.toISOString();
    setIsPolling(true);
    setLastError(null);

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let lingerStartedAt: number | null = null;

    const tick = async () => {
      if (cancelled) return;

      try {
        const user = auth.currentUser;
        if (!user) throw new Error("Not signed in");
        const token = await user.getIdToken();
        const url = new URL(FUNCTION_URLS.functionLogs);
        url.searchParams.set("function_name", functionName);
        if (sinceRef.current) url.searchParams.set("since", sinceRef.current);
        const res = await fetch(url.toString(), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const body = await res.text();
          throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
        }
        const data = (await res.json()) as LogsResponse;
        if (cancelled) return;
        if (data.entries.length > 0) {
          setEntries((prev) => [...prev, ...data.entries]);
          sinceRef.current = data.latest_timestamp;
        }
        setLastError(null);
      } catch (err) {
        if (!cancelled) {
          setLastError(err instanceof Error ? err.message : String(err));
        }
      }

      if (cancelled) return;
      const stillActive = activeRef.current;
      if (stillActive) {
        lingerStartedAt = null;
      } else if (lingerStartedAt === null) {
        lingerStartedAt = Date.now();
      }
      const lingerDone =
        lingerStartedAt !== null && Date.now() - lingerStartedAt >= POLL_LINGER_MS;
      if (!stillActive && lingerDone) {
        setIsPolling(false);
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      setIsPolling(false);
    };
  }, [functionName, startTime]);

  const reset = () => {
    setEntries([]);
    setLastError(null);
    sinceRef.current = null;
  };

  return { entries, isPolling, lastError, reset };
}
